/**
 * Trading key cache for the current browser origin.
 *
 * The key is derived deterministically from the connected wallet (see
 * `trader-crypto.js` `deriveTradingKeyFromWallet`) - the same wallet always
 * reproduces the same key. We still persist it in `localStorage` so a page
 * refresh does not force another wallet signature just to trade again.
 *
 * Use "Clear key" in Settings when you want this origin's copy gone (shared
 * machine, debugging a commitment mismatch, etc.). Re-connect from the
 * wallet to re-derive the same key.
 */
export const STORAGE_KEY = "prediction-market-portal.trading-key";

let cachedTradingKey = null;

/** @returns {object | null} */
export function loadTradingKey() {
  if (cachedTradingKey) return cachedTradingKey;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    cachedTradingKey = JSON.parse(raw);
    return cachedTradingKey;
  } catch {
    return null;
  }
}

/** @param {object} key */
export function saveTradingKey(key) {
  cachedTradingKey = key;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(key));
  } catch {
    // private mode / quota - memory cache still works for this page session
  }
}

export function clearTradingKey() {
  cachedTradingKey = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function downloadTradingKeyBackup(key) {
  const blob = new Blob([JSON.stringify(key, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `pm-trading-key-${(key.commitment_hex || "backup").slice(0, 8)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Parse + validate an Export backup JSON object (or string).
 * @param {string | object} raw
 * @returns {{ commitment_hex: string, pk_base58: string, sk_hex: string, pk_hex?: string }}
 */
export function parseTradingKeyBackup(raw) {
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      throw new Error("Backup file is not valid JSON.");
    }
  }
  if (!obj || typeof obj !== "object") {
    throw new Error("Backup file must be a JSON object.");
  }
  const commitment_hex = String(obj.commitment_hex || "")
    .trim()
    .toLowerCase();
  const sk_hex = String(obj.sk_hex || "")
    .trim()
    .toLowerCase();
  const pk_base58 = String(obj.pk_base58 || "").trim();
  const pk_hex = obj.pk_hex != null ? String(obj.pk_hex).trim().toLowerCase() : undefined;

  if (!/^[0-9a-f]{64}$/.test(commitment_hex)) {
    throw new Error("Backup missing a valid 32-byte commitment_hex.");
  }
  if (!/^[0-9a-f]{64}$/.test(sk_hex)) {
    throw new Error("Backup missing a valid 32-byte sk_hex.");
  }
  if (!pk_base58) {
    throw new Error("Backup missing pk_base58.");
  }

  /** @type {{ commitment_hex: string, pk_base58: string, sk_hex: string, pk_hex?: string }} */
  const key = { commitment_hex, sk_hex, pk_base58 };
  if (pk_hex && /^[0-9a-f]+$/.test(pk_hex)) key.pk_hex = pk_hex;
  return key;
}

/**
 * Refuse import when the participant already has a different linked commitment
 * (same safety net as Connect - would orphan positions).
 * @param {{ commitment_hex: string }} key
 * @param {{ trading_commitment_hex?: string | null } | null | undefined} participant
 */
export function assertBackupMatchesLinkedCommitment(key, participant) {
  const linked = participant?.trading_commitment_hex;
  if (!linked) return;
  if (linked.toLowerCase() !== key.commitment_hex.toLowerCase()) {
    throw new Error(
      "This backup's commitment does not match the commitment already linked to your participant. " +
        "Not importing - that would orphan existing positions. " +
        "Use the backup for the linked key, or continue with a different Moonlight account."
    );
  }
}

/**
 * Parse backup text, enforce linked-commitment match, persist.
 * @param {{ text: string, loadParticipant: () => object | null, saveTradingKey: (key: object) => void }} opts
 */
export function importTradingKeyBackup({ text, loadParticipant, saveTradingKey: save }) {
  const key = parseTradingKeyBackup(text);
  assertBackupMatchesLinkedCommitment(key, loadParticipant());
  save(key);
  return key;
}
